import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, errMsg } from "@/lib/api";

const loadRzp = () => new Promise((res, rej) => {
  if (window.Razorpay) return res();
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = res;
  s.onerror = rej;
  document.body.appendChild(s);
});

const STATUS_TEXT = {
  paid: "Payment verified. Thank you.",
  sent: "Your report has been emailed to you.",
  pending: "Your report is being thoughtfully prepared and will usually reach your inbox within 24 hours of your order.",
  awaiting_ruleset: "Your report is being thoughtfully prepared and will usually reach your inbox within 24 hours of your order.",
};
const GIFT_NOTE = "Every Namonics™ report is composed with care. Your gift will usually reach its recipient within 24 hours of placing your order — and we'll let you know when it arrives.";
const DELIVERY_NOTE = "Every Namonics™ report is composed with care. Yours will usually arrive in your inbox within 24 hours of placing your order.";

function OrderStatus({ order, email, paid, verifying }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    const tick = () => api.get(`/orders/${order.ref}/status`, { params: { email } }).then((r) => setSt(r.data)).catch(() => {});
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, [order.ref, email, paid]);
  if (verifying) return <p className="mt-6 text-sm text-stone-600" data-testid="payment-verifying">Verifying your payment securely…</p>;
  if (!st) return null;
  return (
    <div className="mt-6 border-l-2 border-[#B8860B] pl-4 text-sm text-stone-700" data-testid="payment-status">
      <p>{st.payment_status === "paid" ? STATUS_TEXT.paid : st.payment_status === "awaiting_confirmation" ? "Awaiting confirmation of your PayPal payment." : `Payment status: ${st.payment_status.replaceAll("_", " ")}`}</p>
      {st.payment_status === "paid" && <p className="mt-1" data-testid="payment-report-status">{st.is_gift ? (st.email_status === "sent" ? "Your gift has been delivered to the recipient." : "Your gift is being thoughtfully prepared and will usually reach the recipient within 24 hours of your order.") : st.email_status === "sent" ? STATUS_TEXT.sent : STATUS_TEXT.pending}</p>}
    </div>
  );
}

function PayPalMe({ order, url }) {
  const [started, setStarted] = useState(order.payment_status === "awaiting_confirmation");
  const amt = Number(order.amount).toFixed(2).replace(/\.00$/, "");
  const copy = () => navigator.clipboard.writeText(order.ref).then(() => toast.success("Order number copied"));
  const go = () => { window.open(`${url}/${amt}${order.currency}`, "_blank", "noopener"); api.post("/payments/paypalme/started", { ref: order.ref }).then(() => setStarted(true)).catch((e) => toast.error(errMsg(e))); };
  return (
    <div className="max-w-lg space-y-4" data-testid="paypalme-panel">
      <ol className="list-decimal space-y-2 pl-5 text-sm text-stone-700">
        <li>Press the button below — PayPal opens with <strong>{order.currency} {amt}</strong> already filled in.</li>
        <li>Optional but helpful: paste your order number in the PayPal note.
          <span className="mt-2 flex items-center gap-2"><code className="bg-white px-3 py-1.5 font-mono-ui text-stone-900" data-testid="paypalme-ref">{order.ref}</code>
            <button type="button" onClick={copy} className="rounded-full border border-stone-300 px-3 py-1.5 text-xs hover:border-stone-900" data-testid="paypalme-copy-btn">Copy</button></span></li>
        <li>Complete the payment in PayPal. We confirm it personally, and your report is then written and emailed.</li>
      </ol>
      <button onClick={go} data-testid="paypalme-pay-btn" className="rounded-full bg-stone-900 px-7 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors hover:bg-[#5B2C36]">
        Pay {order.currency} {amt} with PayPal →
      </button>
      {started && <p className="text-sm text-emerald-800" data-testid="paypalme-started-note">Thank you. Once we confirm your PayPal payment, your receipt is emailed and your report usually reaches you within 24 hours of confirmation. You can close this page.</p>}
    </div>
  );
}

export default function Payment({ order, email }) {
  const [cfg, setCfg] = useState(null);
  const [paid, setPaid] = useState(order.payment_status === "paid");
  const [verifying, setVerifying] = useState(false);
  useEffect(() => { api.get("/payments/config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;

  const payRzp = async () => {
    try {
      await loadRzp();
      const o = (await api.post("/payments/razorpay/order", { ref: order.ref })).data;
      const rz = new window.Razorpay({
        key: o.key_id, order_id: o.order_id, amount: o.amount, currency: o.currency, name: "Namonics™", description: `Report ${order.ref}`,
        image: "/namonics-logo-sm.png", prefill: { email: o.email, name: o.name }, theme: { color: "#1C1917" },
        handler: async (resp) => {
          setVerifying(true);
          try { await api.post("/payments/razorpay/verify", { ref: order.ref, ...resp }); setPaid(true); } catch (e) { toast.error(errMsg(e)); } finally { setVerifying(false); }
        },
        modal: { ondismiss: () => api.post("/payments/cancelled", { ref: order.ref, reason: "dismissed" }) },
      });
      rz.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed. You have not been charged."));
      rz.open();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const free = order.provider === "promo";
  const useMe = order.provider === "paypal" && !!cfg.paypal_me_url;
  const useRzp = order.provider === "razorpay" && cfg.razorpay_enabled;
  const usePp = order.provider === "paypal" && cfg.paypal_enabled;
  return (
    <div className="mt-8" data-testid="payment-panel">
      {cfg.test_mode && <p className="mb-3 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8B5A2B]" data-testid="test-mode-badge">Test mode — no real money is charged</p>}
      <p className="mb-5 max-w-lg font-serif-display text-lg italic leading-snug text-stone-700" data-testid="delivery-note">{(order.is_gift ? GIFT_NOTE : DELIVERY_NOTE).replace("of placing your order", useMe ? "of your payment being confirmed" : "of placing your order")}</p>
      {!paid && useRzp && (
        <button onClick={payRzp} data-testid="razorpay-pay-btn" className="rounded-full bg-stone-900 px-7 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors hover:bg-[#5B2C36]">
          Pay {order.currency} {Number(order.amount).toFixed(2)} securely with Razorpay →
        </button>
      )}
      {!paid && usePp && (
        <div className="max-w-sm" data-testid="paypal-buttons">
          <PayPalScriptProvider options={{ clientId: cfg.paypal_client_id, currency: order.currency, intent: "capture" }}>
            <PayPalButtons style={{ layout: "vertical", shape: "pill", color: "black" }}
              createOrder={async () => (await api.post("/payments/paypal/order", { ref: order.ref })).data.id}
              onApprove={async (d) => { setVerifying(true); try { await api.post("/payments/paypal/capture", { ref: order.ref, paypal_order_id: d.orderID }); setPaid(true); } catch (e) { toast.error(errMsg(e)); } finally { setVerifying(false); } }}
              onCancel={() => api.post("/payments/cancelled", { ref: order.ref, reason: "paypal_cancel" })}
              onError={() => toast.error("PayPal could not complete the payment. You have not been charged.")} />
          </PayPalScriptProvider>
        </div>
      )}
      {!paid && useMe && <PayPalMe order={order} url={cfg.paypal_me_url} />}
      {!free && !useRzp && !usePp && !useMe && <p className="text-sm text-stone-600" data-testid="payment-unavailable">Secure {order.provider === "razorpay" ? "Razorpay" : "PayPal"} checkout is being configured. Your order is reserved under reference {order.ref}.</p>}
      <OrderStatus order={order} email={email} paid={paid} verifying={verifying} />
    </div>
  );
}
